import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
/** @import { TargetedMouseEvent } from "preact"; */

import { adminMachineResponse } from "~/backend/validate.js";
/** @import WS from "~/lib/ws/ws.js"; */
import { query, paginatedResponse } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { SearchParams } from "~/lib/search.js";
import * as msg from "~/lib/ws/msg.js";
import html from "~/util/html.js";
import { search } from "~/util/router.js";
/** @import { RouteParams } from "~/util/router.js"; */

import Button from "~/component/Button.js";
import Icon from "~/component/Icon.js";
import WSListener from "~/component/WSListener.js";
import AssignedToFilter from "~/admin/component/AssignedToFilter.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import { ComboboxInput } from "~/admin/component/Combobox.js";
import Pagination from "~/admin/component/Pagination.js";
import ComputersDetail from "./ComputersDetail.js";
/** @import { orgResponse } from "~/backend/validate.js"; */
import adminStyle from "~/admin/lib/style.js";
import { deleteComputers } from "~/admin/res/computer/actions/deleteComputers.js";
import { setAssignee } from "~/admin/res/computer/actions/setAssignee.js";
import { setLocation } from "~/admin/res/computer/actions/setLocation.js";
import ComputerTable from "~/admin/res/computer/ComputerTable.js";
import util from "~/style/util.js";

/**
 * @extends {Component<Props>}
 *
 * @typedef {{ org: orgResponse; ws: WS } & RouteParams<{ computerId: string | undefined }>} Props
 */
export default class ComputersRoute extends Component {
  render() {
    const machineId = this.props.params.value.computerId;
    return [__h(Crumb, {}, machineId ? __h("a", {
      href: "/org/" + this.props.org.id + "/computers"
    }, "Computers") : "Computers"), machineId ? __h(ComputersDetail, {
      org: this.props.org,
      machineId: machineId,
      ws: this.props.ws
    }) : __h(Computers, {
      org: this.props.org,
      ws: this.props.ws
    })];
  }
}

/**
 * @extends {Component<ComputersProps>}
 *
 * @typedef {object} ComputersProps
 * @property {orgResponse} org
 * @property {WS} ws
 */
class Computers extends Component {
  checked = signal(/** @type {Map<string, adminMachineResponse>} */new Map());
  paginate = new Pagination.State();
  sort = ComputerTable.sort();
  machines = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/machines`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      q: SearchParams.q.value,
      group_id: search.value.group_id,
      user_id: search.value.user_id,
      location_id: search.value.location_id
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(adminMachineResponse)
  });
  render() {
    const orgId = this.props.org.id;
    return [__h("div", {
      "class": adminStyle.main
    }, __h("div", {
      "class": adminStyle.search
    }, __h(AssignedToFilter, {
      org: this.props.org
    }), __h(ComboboxInput, {
      query: SearchParams.q,
      debounce: 200
    })), __h("div", {
      "class": adminStyle.body
    }, __h(ComputersActions, {
      orgId: orgId,
      checked: this.checked
    }), __h("div", {
      "class": util.card + " " + adminStyle.tableCard
    }, h(ComputerTable, {
      orgId,
      checked: ComputersActions.canEdit(orgId) ? this.checked : undefined,
      paginate: this.paginate,
      sort: this.sort,
      query: this.machines,
      empty: SearchParams.q.value ? `No computers match "${SearchParams.q.value}".` : [__h("strong", {}, "No computers found."), identity.permission(orgId, "machine_keys", "edit") && __h("div", {}, "New to Phaze? ", __h("a", {
        href: "/org/" + orgId + "/keys"
      }, "Create a key"), ", then use it\n\t\t\t\t\t\t\t\t\t\t\tto install Phaze on a new computer.")]
    })))), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.ADMIN_MACHINE_REFRESH, msg.ADMIN_CONNECTION_REFRESH],
      on: () => this.machines.fetch()
    })];
  }
}

/**
 * @extends {Component<ComputersActionsProps>}
 *
 * @typedef {object} ComputersActionsProps
 * @property {string} orgId
 * @property {typeof Computers.prototype.checked} checked
 */
class ComputersActions extends Component {
  /** @param {string} orgId */
  static canEdit(orgId) {
    return identity.permission(orgId, "machines", "edit");
  }
  render() {
    if (!ComputersActions.canEdit(this.props.orgId)) return null;
    const checked = this.props.checked;
    return __h("menu", {
      "class": adminStyle.menu
    }, __h(Button, {
      disabled: checked.value.size === 0,
      size: "small",
      onClick: async (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => {
        const res = await setAssignee({
          anchor: event.currentTarget,
          placement: "bottom-start",
          orgId: this.props.orgId
        }, ...checked.value.values());
        if (res) checked.value = new Map();
      }
    }, __h(Icon, {
      name: "pencil",
      size: 12
    }), "Assign to"), __h(Button, {
      disabled: !(checked.value.size > 0),
      size: "small",
      onClick: async (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => {
        const res = await setLocation({
          anchor: event.currentTarget,
          placement: "bottom-start",
          orgId: this.props.orgId
        }, ...checked.value.values());
        if (res) checked.value = new Map();
      }
    }, __h(Icon, {
      name: "pencil",
      size: 12
    }), "Edit location"), __h(Button, {
      size: "small",
      kind: "negative",
      disabled: !(checked.value.size > 0),
      onClick: async () => {
        const res = await deleteComputers({
          orgId: this.props.orgId
        }, ...checked.value.keys());
        if (res) checked.value = new Map();
      }
    }, __h(Icon, {
      name: "trash",
      size: 12
    }), "Remove"));
  }
}